'use client'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import Head from 'next/head'

// Type declarations for cookie platforms
declare global {
  interface Window {
    ckyShow?: () => void;
    OneTrust?: {
      ToggleInfoDisplay: () => void;
    };
    Cookiebot?: {
      show: () => void;
    };
  }
}

const CookiePolicy = () => {
  const [scrollProgress, setScrollProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (window.scrollY / totalHeight) * 100
      setScrollProgress(progress)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleCookieSettings = () => {
    try {
      // Option 1: If using CookieYes
      if (typeof window !== 'undefined' && window.ckyShow) {
        window.ckyShow();
        return;
      }

      // Option 2: If using OneTrust
      if (typeof window !== 'undefined' && window.OneTrust?.ToggleInfoDisplay) {
        window.OneTrust.ToggleInfoDisplay();
        return;
      }

      // Option 3: If using Cookiebot
      if (typeof window !== 'undefined' && window.Cookiebot?.show) {
        window.Cookiebot.show();
        return;
      }

      // Option 4: If using custom cookie banner with data attributes
      const cookieBanner = document.querySelector('[data-cookie-settings]') as HTMLElement;
      if (cookieBanner?.click) {
        cookieBanner.click();
        return;
      }

      // Option 5: If using custom cookie banner with specific class
      const customBanner = document.querySelector('.cookie-settings-trigger') as HTMLElement;
      if (customBanner?.click) {
        customBanner.click();
        return;
      }

      // Option 6: Try to find any cookie consent related elements
      const cookieElements = [
        '[id*="cookie"]',
        '[class*="cookie"]',
        '[id*="consent"]',
        '[class*="consent"]'
      ];

      for (const selector of cookieElements) {
        const element = document.querySelector(selector) as HTMLElement;
        if (element && typeof element.click === 'function') {
          element.click();
          return;
        }
      }

      // Fallback: Alert user if no cookie system is detected
      alert('Cookie settings are not available. Please check your browser settings to manage cookies.');
    } catch (error) {
      console.error('Error opening cookie settings:', error);
      alert('Unable to open cookie settings. Please check your browser settings to manage cookies.');
    }
  }

  return (
    <>
      <Head>
        <title>Cookie Policy - Infinitcode</title>
        <meta name="description" content="Cookie Policy for Infinitcode - Learn about how we use cookies and manage your cookie preferences." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <style jsx>{`
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }

        .animated-border {
          position: relative;
          background: linear-gradient(#041536, #041536) padding-box,
                      conic-gradient(from var(--angle), transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%) border-box;
          border: 2px solid transparent;
          border-radius: 1rem;
          animation: spin 8s linear infinite;
        }

        .glow-effect {
          position: absolute;
          inset: -15px;
          background: conic-gradient(from var(--angle), transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%);
          border-radius: 1.5rem;
          filter: blur(15px);
          opacity: 0.3;
          animation: spin 8s linear infinite;
          z-index: -1;
          pointer-events: none;
        }

        @keyframes spin {
          from {
            --angle: 0deg;
          }
          to {
            --angle: 360deg;
          }
        }

        /* Fallback for browsers that don't support @property or custom properties in gradients */
        @supports not (background: conic-gradient(from var(--angle), red, blue)) {
          .animated-border {
            background: #041536;
            border: none;
            overflow: hidden;
          }

          .animated-border::before {
            content: '';
            position: absolute;
            inset: -2px;
            background: conic-gradient(from 0deg at 50% 50%, transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%);
            border-radius: inherit;
            z-index: -1;
            animation: rotateFallback 8s linear infinite;
          }

          .animated-border::after {
            content: '';
            position: absolute;
            inset: 2px;
            background: #041536;
            border-radius: calc(1rem - 2px);
            z-index: -1;
          }

          .glow-effect {
            background: conic-gradient(from 0deg at 50% 50%, transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%);
            animation: rotateFallback 8s linear infinite;
          }

          @keyframes rotateFallback {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        }

        .floating-shapes {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: -1;
        }

        .shape {
          position: absolute;
          opacity: 0.1;
          animation: float 20s infinite linear;
        }

        .shape:nth-child(1) {
          top: 30%;
          left: 15%;
          animation-delay: 0s;
        }

        .shape:nth-child(2) {
          top: 70%;
          right: 15%;
          animation-delay: -10s;
        }

        .shape:nth-child(3) {
          bottom: 30%;
          left: 25%;
          animation-delay: -5s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        .section-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, #3BCF8E, transparent);
          margin: 2rem 0;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #041536;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #3BCF8E, #2DA771);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #2DA771, #3BCF8E);
        }

        .cookie-settings-btn {
          background: linear-gradient(135deg, #3BCF8E, #2DA771);
          transition: all 0.3s ease;
        }

        .cookie-settings-btn:hover {
          background: linear-gradient(135deg, #2DA771, #3BCF8E);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(59, 207, 142, 0.3);
        }
      `}</style>

      <div className="bg-gradient-to-br from-[#020F2A] via-[#041536] to-[#020F2A] text-white min-h-screen">
        {/* Scroll Progress Indicator */}
        <div 
          className="fixed top-0 left-0 h-1 bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] z-50 transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
        
        {/* Floating Background Shapes */}
        <div className="floating-shapes">
          <div className="shape w-24 h-24 bg-gradient-to-r from-[#3BCF8E]/20 to-[#2DA771]/20 rounded-full"></div>
          <div className="shape w-18 h-18 bg-gradient-to-r from-[#2DA771]/20 to-[#3BCF8E]/20 rounded-lg rotate-45"></div>
          <div className="shape w-14 h-14 bg-[#3BCF8E]/20 rounded-full"></div>
        </div>

        {/* Header */}
        <header className="relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 py-16">
            {/* Back Button */}
            <div className="mb-8">
              <Link 
                href="/"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-[#3BCF8E] transition-all duration-300 group"
              >
                <svg 
                  className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>

            <div className="text-center">
              <div className="relative">
                <div className="glow-effect"></div>
                <div className="animated-border p-12 bg-gradient-to-br from-[#041536]/90 to-[#020F2A]/90">
                  <h1 className="text-5xl md:text-6xl font-bold mb-4">
                    Cookie{' '}
                    <span className="bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-transparent bg-clip-text">
                      Policy
                    </span>
                  </h1>
                  <div className="text-lg flex justify-center text-gray-300 gap-10">
                    <p>
                      <span className="text-[#3BCF8E] font-semibold">Effective Date:</span> 30-Jun-2025
                    </p>
                    <p>
                      <span className="text-[#3BCF8E] font-semibold">Last Updated:</span> 30-Jun-2025
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-6 pb-16">
          {/* What are cookies? */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/60 to-[#020F2A]/60 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/20">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">What are cookies?</h2>
              <div className="space-y-4 text-gray-200 leading-relaxed">
                <p>
                  This Cookie Policy explains what cookies are and how we use them, the types of cookies we use i.e, 
                  the information we collect using cookies and how that information is used, and how to manage the cookie settings.
                </p>
                <p>
                  Cookies are small text files that are used to store small pieces of information. They are stored on your 
                  device when the website is loaded on your browser. These cookies help us make the website function properly, 
                  make it more secure, provide better user experience, and understand how the website performs and to analyze 
                  what works and where it needs improvement.
                </p>
              </div>
            </div>
          </section>

          <div className="section-divider"></div>

          {/* How do we use cookies? */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">How do we use cookies?</h2>
              <div className="space-y-4 text-gray-200 leading-relaxed">
                <p>
                  As most of the online services, our website uses first-party and third-party cookies for several purposes. 
                  First-party cookies are mostly necessary for the website to function the right way, and they do not collect 
                  any of your personally identifiable data.
                </p>
                <p>
                  The third-party cookies used on our website are mainly for understanding how the website performs, how you 
                  interact with our website, keeping our services secure, providing advertisements that are relevant to you, 
                  and all in all providing you with a better and improved user experience and help speed up your future 
                  interactions with our website.
                </p>
              </div>
            </div>
          </section>

          <div className="section-divider"></div>

          {/* Types of Cookies */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">Types of Cookies we use</h2>
              
              {/* Cookie Types Table */}
              <div className="overflow-x-auto">
                <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-xl p-6 border border-[#3BCF8E]/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-[#3BCF8E]">Essential Cookies</h3>
                      <p className="text-gray-300 text-sm">
                        These cookies are necessary for the website to function and cannot be switched off. 
                        They enable core functionality such as security, network management, and accessibility.
                      </p>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-[#3BCF8E]">Analytics Cookies</h3>
                      <p className="text-gray-300 text-sm">
                        These cookies help us understand how visitors interact with our website by collecting 
                        and reporting information anonymously.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="section-divider"></div>

          {/* Manage Cookie Preferences */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">Manage cookie preferences</h2>

              <div className="space-y-4 text-gray-200 leading-relaxed">
                <p>
                  Different browsers provide different methods to block and delete cookies used by websites. 
                  You can change the settings of your browser to block/delete the cookies. Listed below are the links to the 
                  support documents on how to manage and delete cookies from the major web browsers.
                </p>
                
                {/* Browser Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="bg-gradient-to-r from-[#3BCF8E]/5 to-[#2DA771]/5 rounded-lg p-4 border border-[#3BCF8E]/10">
                    <h4 className="font-semibold text-[#3BCF8E] mb-2">Chrome:</h4>
                    <Link 
                      href="https://support.google.com/accounts/answer/32050" 
                      target="_blank"
                      className="text-gray-300 hover:text-[#3BCF8E] transition-colors text-sm break-all"
                    >
                      https://support.google.com/accounts/answer/32050
                    </Link>
                  </div>
                  <div className="bg-gradient-to-r from-[#3BCF8E]/5 to-[#2DA771]/5 rounded-lg p-4 border border-[#3BCF8E]/10">
                    <h4 className="font-semibold text-[#3BCF8E] mb-2">Safari:</h4>
                    <Link 
                      href="https://support.apple.com/en-in/guide/safari/sfri11471/mac" 
                      target="_blank"
                      className="text-gray-300 hover:text-[#3BCF8E] transition-colors text-sm break-all"
                    >
                      https://support.apple.com/en-in/guide/safari/sfri11471/mac
                    </Link>
                  </div>
                  <div className="bg-gradient-to-r from-[#3BCF8E]/5 to-[#2DA771]/5 rounded-lg p-4 border border-[#3BCF8E]/10">
                    <h4 className="font-semibold text-[#3BCF8E] mb-2">Firefox:</h4>
                    <Link 
                      href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" 
                      target="_blank"
                      className="text-gray-300 hover:text-[#3BCF8E] transition-colors text-sm break-all"
                    >
                      Mozilla Firefox Support
                    </Link>
                  </div>
                  <div className="bg-gradient-to-r from-[#3BCF8E]/5 to-[#2DA771]/5 rounded-lg p-4 border border-[#3BCF8E]/10">
                    <h4 className="font-semibold text-[#3BCF8E] mb-2">Internet Explorer:</h4>
                    <Link 
                      href="https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc" 
                      target="_blank"
                      className="text-gray-300 hover:text-[#3BCF8E] transition-colors text-sm break-all"
                    >
                      Microsoft Support
                    </Link>
                  </div>
                </div>

                <p className="mt-6">
                  If you are using any other web browser, please visit your browser&apos;s official support documents.
                </p>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  )
}

export default CookiePolicy